// Casual rating engine. A thin, framework-agnostic wrapper over OpenSkill
// (Plackett-Luce) so the SAME code can run on the server (authoritative match
// result → rating update) and on the client (display helpers). The server is
// the only thing that should ever CALL rateMatch — the client just reads.
//
// We use OpenSkill rather than classic Elo because our games are N-player
// free-for-alls, not 1v1: OpenSkill consumes a whole finishing order at once
// and tracks per-player uncertainty (sigma), so new players settle quickly.
import { rating as osRating, rate as osRate, ordinal as osOrdinal } from "openskill";

export interface PlayerRating {
  mu: number; // skill estimate
  sigma: number; // uncertainty (starts high, shrinks with games)
}

// A brand-new (unproven) rating.
export function newRating(): PlayerRating {
  const r = osRating();
  return { mu: r.mu, sigma: r.sigma };
}

// OpenSkill's conservative skill estimate (mu - 3·sigma). Good for sorting a
// leaderboard ("proven at least this good"), but it can go negative for new
// players, so it's not what we show.
export function ordinal(r: PlayerRating): number {
  return osOrdinal({ mu: r.mu, sigma: r.sigma });
}

// Friendly display number, centered so the population average (mu ≈ 25, also a
// brand-new player's value) maps to 1000 — the middle of the tier range — so a
// real, bell-curved player base clusters at the mid tier. OpenSkill keeps the
// average mu ≈ 25 (winners' gains ≈ losers' losses), which is why centering
// there is stable. Floored so very weak ratings never go negative. Hidden in
// casual, revealed in Ranked.
const RATING_CENTER = 1000;
const RATING_SCALE = 40; // display points per mu point away from average (wide, so tier bounds land on round hundreds)
// Chess-style ceiling: the display scale is linear up to SOFT_CAP_START, then
// compresses asymptotically toward SOFT_CAP_START + SOFT_CAP_SPAN (3050). Like
// chess, the 2900s are legendary and 3000+ is near-mythical (a raw ~3450, mu
// ≈ 86, is needed to poke over 3000). Purely presentational and monotonic, so
// ordering/tiers below 2400 are untouched. Keep in sync with the server's
// engine.js.
const SOFT_CAP_START = 2400;
const SOFT_CAP_SPAN = 650;
export function displayRating(r: PlayerRating): number {
  const raw = RATING_CENTER + (r.mu - 25) * RATING_SCALE;
  const capped = raw <= SOFT_CAP_START ? raw : SOFT_CAP_START + SOFT_CAP_SPAN * Math.tanh((raw - SOFT_CAP_START) / SOFT_CAP_SPAN);
  return Math.max(100, Math.round(capped));
}

// (There is deliberately no "provisional / Placing…" state: everyone shows a
// tier from their very first game. sigma still does its statistical job inside
// OpenSkill — early ratings simply move faster.)

// Update ratings from ONE finished match. `finishers` are the RATED
// participants (signed-in players + rated bots) in finishing order, winner
// first; guests must be filtered out by the caller so they never affect ratings.
// `ranks` (optional, lower = better, equal = tie) handles ties; omit for a
// strict 1..N order. Returns each input with its `rating` replaced.
export function rateMatch<T extends { rating: PlayerRating }>(finishers: T[], ranks?: number[]): T[] {
  if (finishers.length < 2) return finishers; // nothing to learn from < 2 rated players
  const teams = finishers.map((f) => [{ mu: f.rating.mu, sigma: f.rating.sigma }]);
  const updated = ranks ? osRate(teams, { rank: ranks }) : osRate(teams);
  return finishers.map((f, i) => ({
    ...f,
    rating: { mu: updated[i][0].mu, sigma: updated[i][0].sigma },
  }));
}
